import { AionzSnackbar, ExclusiveSidePanelService } from '@aionzdev/design-system';
import {
  AbstractNotificationsDrawerService,
  NotificationItemView,
  PurchaseRequestStatus
} from '@aionzdev/modules';
import { Injectable, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter } from 'rxjs';
import { AbstractPurchasingService } from '../../pages/purchasing-manager';
import { NotificationsService } from './notifications.service';

export const AI_CHAT_PANEL_ID = 'ai-agent-chat';
export const NOTIFICATIONS_PANEL_ID = 'notifications-drawer';

/**
 * Estado do drawer de notificações do admin. É consumido pelo `aionz-notification-drawer-host` do
 * @aionzdev/modules via AbstractNotificationsDrawerService; o sino fica no sidebar do layout.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDrawerStateService extends AbstractNotificationsDrawerService {
  private readonly notifications = inject(NotificationsService);
  private readonly exclusiveSidePanel = inject(ExclusiveSidePanelService);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly router = inject(Router);
  private readonly snackbar = inject(AionzSnackbar);

  readonly enabled = signal(true).asReadonly();
  readonly totalNotOpen = toSignal(this.notifications.totalNotOpen$, { initialValue: 0 });
  readonly notificationsList = this.notifications.notificationsList;
  readonly loading = this.notifications.loading;
  readonly loadingMore = this.notifications.loadingMore;
  readonly hasMore = this.notifications.hasMore;
  readonly loadError = this.notifications.loadError;
  readonly drawerOpen = signal(false);

  constructor() {
    super();

    effect(() => {
      if (this.drawerOpen()) {
        untracked(() => this.exclusiveSidePanel.announceOpen(NOTIFICATIONS_PANEL_ID));
      } else {
        untracked(() => this.notifications.cancelLoading());
      }
    });

    // outro painel lateral abriu: o chat de IA fecha o drawer pelo próprio layout
    this.exclusiveSidePanel.opened$
      .pipe(filter(id => id !== AI_CHAT_PANEL_ID && id !== NOTIFICATIONS_PANEL_ID))
      .subscribe(() => this.closeDrawer());
  }

  toggleDrawer(): void {
    if (this.drawerOpen()) {
      this.closeDrawer();
      return;
    }

    this.drawerOpen.set(true);
    this.notifications.fetchNotificationsList().subscribe({
      error: () => this.snackbar.error('Não foi possível carregar as notificações.')
    });
  }

  closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  loadMore(retry = false): void {
    if (!this.drawerOpen()) return;

    this.notifications.fetchNextPage(retry).subscribe({
      error: () => this.snackbar.error('Não foi possível carregar as notificações.')
    });
  }

  markAllAsRead(): void {
    this.notifications.markAllNotificationsAsRead().subscribe({
      error: () => this.snackbar.error('Não foi possível marcar as notificações como lidas.')
    });
  }

  openItem(item: NotificationItemView): void {
    const alert = this.notifications.findAlert(item.id);
    if (!alert) return;

    if (!alert.isOpen) {
      this.notifications.markNotificationAsRead(alert._id).subscribe({
        error: () => this.snackbar.error('Não foi possível marcar a notificação como lida.')
      });
    }

    if (alert.resource) {
      const { type, id } = alert.resource;
      if (type === 'structured_offer' && Number.isSafeInteger(id) && id > 0) {
        this.openPurchaseNotification(String(id));
        return;
      }

      this.navigateToPurchasingBoard();
      return;
    }

    if (alert.url) {
      this.closeDrawer();
      void this.router.navigateByUrl(alert.url);
    }
  }

  private openPurchaseNotification(purchaseId: string): void {
    this.purchasingService.findPurchaseById(purchaseId).subscribe({
      next: response => {
        const purchase = response.body;
        if (!purchase) {
          this.snackbar.error('Não foi possível localizar a solicitação da notificação.');
          this.navigateToPurchasingBoard();
          return;
        }

        this.purchasingService.setCache(purchaseId, purchase);
        const destination = this.getPurchaseNotificationDestination(purchaseId, purchase.status);
        this.closeDrawer();
        void this.router.navigateByUrl(destination);
      },
      error: () => {
        this.snackbar.error('Não foi possível identificar o status da solicitação.');
        this.navigateToPurchasingBoard();
      }
    });
  }

  private getPurchaseNotificationDestination(
    purchaseId: string,
    status: PurchaseRequestStatus[]
  ): string {
    const basePath = '/main/gerenciador-ordem-compra';
    if (status.includes(PurchaseRequestStatus.PENDING)) {
      return `${basePath}/solicitacao-pendente/${purchaseId}`;
    }
    if (status.includes(PurchaseRequestStatus.ACCEPTED)) {
      return `${basePath}/solicitacao-aceita/${purchaseId}`;
    }
    if (status.includes(PurchaseRequestStatus.IN_QUOTATION)) {
      return `${basePath}/mapa-de-cotacao/${purchaseId}`;
    }
    return `${basePath}/quadro`;
  }

  private navigateToPurchasingBoard(): void {
    this.closeDrawer();
    void this.router.navigateByUrl('/main/gerenciador-ordem-compra/quadro');
  }
}
